////////////////////////////////////////////////////////
//                  Outer Packages                    //
////////////////////////////////////////////////////////
import { drawLine, getCanvasCurrentPackage } from './drawCanvas';
import { V4UUID } from '@/firebase/database';
import { useGlobalStore } from '@/modules/globalModule/store';
import { Position } from '../storeClasses/canvasObjects';
import { PackageDetail } from '../../store';
import { PackageT } from '../storeClasses/packageT';
import PACKAGE_IMG_SVG from '@/modules/globalModule/assets/package.svg';

/**
 * gets all elements with a connection to a package outside the current one
 * draws a line from each element to a smaller version of the external package
 * @param stage
 * @returns
 */
export function drawLinesToOuterPackages(stage: any) {
  const pkg = getCanvasCurrentPackage();
  if (!pkg) return;
  let num = 0;
  let linesDrawn: Array<{ from: Position; to: Position }> = []; // to prevent duplicate lines
  const externalElemConnections = getExternalElemConnections(pkg);
  for (const pkgId of Object.keys(externalElemConnections)) {
    drawExternalPackageLine(
      stage,
      pkgId,
      num,
      externalElemConnections[pkgId],
      linesDrawn
    );
    num++;
  }
}
/**
 * Returns a record where each key is a package id, and each value
 *  is all of the elems in the current package that are
 * connected to that external package
 * @param pkg
 */
const getExternalElemConnections = (
  pkg: PackageT
): Record<V4UUID, V4UUID[]> => {
  const globalStore = useGlobalStore();
  const connections: Record<V4UUID, V4UUID[]> = {};
  // getting external node connections of publishers and subscribers
  for (let node of pkg.nodes) {
    for (let nodeTopic of node.subscribers.concat(node.publishers)) {
      const elemPkg = globalStore.getElemPackage(nodeTopic);
      if (!elemPkg) continue;
      if (elemPkg.id !== pkg.id) {
        addPackageAndElemToConnections(connections, elemPkg.id, node.id);
      }
    }
  }
  // getting connections of nodes and external services
  for (let service of globalStore.getServices()) {
    const elemPkg = globalStore.getElemPackage(service.id);
    if (elemPkg && elemPkg.id !== pkg.id) {
      for (let node of pkg.nodes) {
        if (node.id === service.server || service.clients.includes(node.id)) {
          addPackageAndElemToConnections(connections, elemPkg.id, node.id);
        }
      }
    }
  }
  // getting external topic connections
  for (let node of globalStore.getNodes()) {
    for (let topic of pkg.topics) {
      if (node.publishers.concat(node.subscribers).includes(topic.id)) {
        const elemPkg = globalStore.getElemPackage(node.id);
        if (elemPkg && elemPkg.id !== pkg.id)
          addPackageAndElemToConnections(connections, elemPkg.id, topic.id);
      }
    }
  }

  // getting external service connections
  for (let service of pkg.services) {
    let searchArray = [...service.clients];
    if (service.server) searchArray.push(service.server);
    for (let nodeId of searchArray) {
      const elemPkg = globalStore.getElemPackage(nodeId);
      if (elemPkg && elemPkg.id !== pkg.id)
        addPackageAndElemToConnections(connections, elemPkg.id, service.id);
    }
  }

  return connections;
};

const addPackageAndElemToConnections = (
  connections: Record<V4UUID, V4UUID[]>,
  pkgId: V4UUID,
  elemId: V4UUID
) => {
  if (pkgId in connections) {
    if (!connections[pkgId].includes(elemId)) {
      connections[pkgId].push(elemId);
    }
  } else connections[pkgId] = [elemId];
};

const drawExternalPackageLine = (
  stage: any,
  pkgId: V4UUID,
  num: number,
  elemIds: V4UUID[],
  linesDrawn: Array<{ from: Position; to: Position }>
) => {
  const globalStore = useGlobalStore();
  const pkg = globalStore.getPackage(pkgId);
  if (!pkg) return;
  if (!stage.getChildByName(pkg.id)) drawOuterPackage(stage, pkg, num);
  const externalPackagePos = { x: 30, y: 30 + num * 70 };
  elemIds.forEach((elemId) => {
    const elem = globalStore.getCanvasElemById(elemId);
    if (
      !elem ||
      linesDrawn.includes({
        from: externalPackagePos,
        to: elem.getPosition()
      })
    )
      return;
    drawLine(stage, { id: pkg.id, position: externalPackagePos }, elem);
    linesDrawn.push({ from: externalPackagePos, to: elem.getPosition() });
  });
};

function drawOuterPackage(
  stage: any,
  packageDetail: PackageDetail,
  displacement: number
) {
  const args = {
    x: 30,
    y: 30,
    margin: 15,
    xTextPadding: 5
  };
  const container = new createjs.Container();
  const packageImg = new createjs.Bitmap(PACKAGE_IMG_SVG);
  const nameText = new createjs.Text(packageDetail.name, '20px Arial');
  packageImg.image.onload = () => {
    const { height, width } = packageImg.getTransformedBounds();
    packageImg.set({ x: args.x - width / 2, y: args.y - height / 2 });
    const hitbox = new createjs.Shape();
    hitbox.graphics.beginFill('#000').drawCircle(width / 2, height / 2, width);
    packageImg.hitArea = hitbox;
    container.addChild(packageImg);

    container.set({
      y:
        displacement *
        (Math.max(
          packageImg.getTransformedBounds().height,
          nameText.getBounds().height
        ) +
          args.margin)
    });
    stage.update();
  };
  packageImg.name = packageDetail.id;
  container.name = packageDetail.id;
  const { height, width } = nameText.getBounds();
  nameText.set({ x: args.xTextPadding, y: args.y + height });
  container.addChild(nameText);
  stage.addChild(container);
  stage.update();
  return container;
}
